import React, { useContext, useEffect } from 'react';
import { useInfiniteHits } from 'react-instantsearch';
import { motion, useMotionValue } from 'framer-motion';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';

import { Context } from '../../../context/provider';

import { AngleUp } from '../../../../../icons/arrow/arrow';

import {
  getColor,
  getSpace,
  px2rem,
  borderClipPath,
  mq
} from '../../../../../../stylesheets/tools';

export const StyledTimeline = styled.div`
  margin-bottom: ${getSpace('xxxs')};

  .timeline__content {
    position: relative;
    width: 100%;
    height: ${px2rem('30px')};
  }

  .timeline__horizontal-line {
    position: absolute;
    top: 50%;
    right: 10px;
    width: 100%;
    margin: 0;
    z-index: -1;
    border: none;
    background: ${getColor('teaserGray')};
    height: 2px;
  }

  .timeline__date {
    cursor: pointer;
    display: inline-block;
    position: absolute;
    width: ${px2rem('20px')};
    height: ${px2rem('30px')};
    ${borderClipPath('#000', '#fff')}
    border-radius: 2px;
    transform-origin: center;
    scale: -1 1;
    transition: all 0.3s ease-in-out;
    transform: translateX(-50%);
    z-index: 1;

    &--today {
      right: 0px;
      transform: none;
    }

    &--active,
    &:hover {
      scale: -1.5 1.5;

      &:after {
        background: ${getColor('berry')};
      }
    }

    &:after {
      border-radius: 2px;
    }
  }

  .timeline__wrapper {
    position: relative;
    margin: ${getSpace('xl')} 0 ${getSpace('xxxs')} 0;
    width: 100vw;
    display: flex;
    justify-content: flex-end;

    ${mq('m')} {
      margin: ${getSpace('xxl')} 0 ${getSpace('xxxs')} 0;
    }
  }

  .timeline__marker-wrapper {
    position: absolute;
    top: -${px2rem('53px')};
    height: ${px2rem('70px')};
    border-left: 2px solid ${getColor('teaserGray')};
  }

  .timeline__marker-wrapper--today {
    position: absolute;
    left: calc(100% - 10px);
  }

  .timeline__marker-year {
    position: absolute;
    white-space: nowrap;
    top: -${px2rem('7px')};
    left: ${px2rem('5px')};
    color: ${getColor('black')};
    font-size: ${px2rem('18px')};
    text-transform: uppercase;
  }

  .timeline__focus-wrapper {
    text-align: center;
  }
`;

const Timeline = () => {
  const {
    scrollPosition,
    isFiltered,
    updateScrollPosition,
    activeIndex,
    activePosition,
    maxScrollPosition,
    setMaxScrollPosition,
    datePositions,
    setDatePositions
  } = useContext(Context);
  const t = useTranslations();
  const { hits, isLastPage, showMore } = useInfiniteHits();

  const dates = hits.map(hit => {
    const posixTimestamp = hit.post_date_timestamp;
    const date = new Date(posixTimestamp * 1000);
    return date;
  });

  const timelineYearOffset = 30;

  const getOffsetInDays = (first, second) => {
    const firstTimestamp = first.getTime() / 1000;
    const secondTimestamp = second.getTime() / 1000;
    const secondsDifference = Math.abs(secondTimestamp - firstTimestamp);
    return Math.floor(secondsDifference / 86400);
  };

  const getPositions = () => {
    let lastRight = 0;
    const WIDTH_PER_DAY = dates.length < 10 ? 100 : 50;

    return dates.map((date, index) => {
      const prevDate = dates.at(index - 1) || new Date();
      const distanceToPrevInDays = getOffsetInDays(date, prevDate);

      let offsetDistance = distanceToPrevInDays;
      if (distanceToPrevInDays > 5) {
        offsetDistance = 5;
      } else if (distanceToPrevInDays < 1) {
        offsetDistance = 1;
      }

      const newRight = lastRight + offsetDistance * WIDTH_PER_DAY;
      lastRight = newRight;
      return newRight;
    });
  };

  const x = useMotionValue('calc(-50% + 0px)');

  const handleScroll = () => {
    const totalHeight = maxScrollPosition - window.innerHeight;
    const currentScroll = window.scrollY;
    const percentage = currentScroll / totalHeight;
    const scrollPercentage = percentage > 1 ? 1 : percentage;
    const position = maxScrollPosition * scrollPercentage;
    const scroll = position > maxScrollPosition ? maxScrollPosition : position;

    x.set(`calc(-50% + ${scroll}px)`);
    updateScrollPosition(scroll);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [maxScrollPosition, scrollPosition]);

  const handleDateClick = (position, behavior = 'smooth') => {
    updateScrollPosition(position);

    if (position === 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const percentageOfPosition = position / maxScrollPosition;
    const totalHeight = maxScrollPosition - window.innerHeight;
    window.scrollTo({ top: totalHeight * percentageOfPosition - 10, behavior });
  };

  const handleActiveAfterFilter = () => {
    if (isFiltered && datePositions.length > 0) {
      handleDateClick(datePositions[0]);
    } else {
      handleDateClick(0, 'instant');
    }
  };

  useEffect(() => {
    handleActiveAfterFilter();
  }, [datePositions, isFiltered]);

  /**
   * Restores position before fetching more hits
   */
  useEffect(() => {
    if (activeIndex === hits.length - 3 && !isLastPage) {
      const position = datePositions[activeIndex];
      handleDateClick(position, 'instant');
      showMore();
    }
  }, [activeIndex]);

  /**
   * Updates datePositions based on given hits
   */
  useEffect(() => {
    if (hits?.length === 0) updateScrollPosition(0);
    const positions = getPositions();

    setDatePositions(positions);
    setMaxScrollPosition(Math.max(...[...positions, window.innerHeight * 2]));
  }, [hits]);

  const lineWidth = maxScrollPosition + window.innerWidth;

  return (
    <StyledTimeline>
      <div className="timeline__wrapper">
        <motion.div className="timeline__content" style={{ x }}>
          <motion.hr
            className="timeline__horizontal-line"
            animate={{
              width: `calc(${lineWidth}px + 0px)`,
              transition: { duration: 0.4 }
            }}
            initial={{ width: 0 }}
          />

          {datePositions.map((position, index) => {
            const currentDate = new Date(dates[index]);
            const nextDate = new Date(dates?.[index + 1] || dates[index]);
            const isActive = activePosition === position;

            return (
              <React.Fragment key={position}>
                <motion.button
                  type="button"
                  className={`timeline__date ${isActive ? 'timeline__date--active' : ''}`}
                  animate={{
                    right: `${position}px`,
                    opacity: 1,
                    transition: { duration: 0.3, delay: 0.2 }
                  }}
                  initial={{ opacity: 0, right: `${position}px` }}
                  exit={{
                    opacity: 0,

                    right: `${position}px`,
                    transition: { delay: 0, duration: 0.2 }
                  }}
                  onClick={() => handleDateClick(position)}
                  tabIndex={0}
                />

                {index === datePositions.length - 1 ||
                currentDate.getFullYear() !== nextDate.getFullYear() ? (
                  <motion.div
                    className="timeline__marker-wrapper"
                    animate={{
                      opacity: 1,
                      right: `${position + timelineYearOffset}px`,
                      y: 0
                    }}
                    initial={{ opacity: 0, right: `${position + timelineYearOffset}px` }}
                    exit={{ opacity: 0, right: `${position + timelineYearOffset}px` }}
                    transition={{
                      right: { delay: 0, duration: 0 },
                      opacity: { duration: 1, delay: 0.5 }
                    }}
                  >
                    <span className="timeline__marker-year">
                      {currentDate?.getFullYear() || ''}
                    </span>
                  </motion.div>
                ) : null}
              </React.Fragment>
            );
          })}

          <div className="timeline__marker-wrapper timeline__marker-wrapper--today">
            <span className="timeline__marker-year">{t('archive.today')}</span>
          </div>

          {!isFiltered && (
            <button
              className={`timeline__date timeline__date--today ${
                activePosition === 0 ? 'timeline__date--active' : ''
              }`}
              type="button"
              onClick={() => handleDateClick(0)}
              tabIndex={0}
            />
          )}
        </motion.div>
      </div>

      <div className="timeline__focus-wrapper">
        <span className="timeline__focus-point">
          <AngleUp />
        </span>
      </div>
    </StyledTimeline>
  );
};

export default Timeline;
